import { useMemo } from 'react';
import { useInfiniteQuery, InfiniteData, QueryKey } from 'react-query';

import { apiService, Memory, MemoryList } from '../services/api';

/** Default page size for browse mode (API max is 100). */
export const MEMORY_LIST_PAGE_SIZE = 50;

export type MemorySortField = 'created_at' | 'updated_at' | 'importance';

export interface InfiniteMemoriesQueryFilters {
  spaceId?: string;
  sortBy?: MemorySortField;
  sortOrder?: 'asc' | 'desc';
  tags?: string[];
  source?: string;
  minImportance?: number;
  pageSize?: number;
  enabled?: boolean;
}

export const infiniteMemoriesBaseKey = 'memories-infinite' as const;

export function getInfiniteMemoriesQueryKey(
  filters: Omit<InfiniteMemoriesQueryFilters, 'enabled'>
): QueryKey {
  const {
    spaceId,
    sortBy = 'created_at',
    sortOrder = 'desc',
    tags,
    source,
    minImportance,
    pageSize = MEMORY_LIST_PAGE_SIZE,
  } = filters;
  const tagKey =
    tags && tags.length > 0 ? [...tags].sort().join('|') : '';
  return [
    infiniteMemoriesBaseKey,
    {
      spaceId: spaceId ?? null,
      sortBy,
      sortOrder,
      tagKey,
      source: source ?? '',
      minImportance: minImportance ?? null,
      pageSize,
    },
  ];
}

/** Pure logic for tests and query options. */
export function getNextMemoryListPageParam(lastPage: MemoryList): number | undefined {
  if (lastPage.pages <= 0) return undefined;
  if (lastPage.page >= lastPage.pages) return undefined;
  return lastPage.page + 1;
}

export function applyMemoryUpdatedToInfiniteData(
  data: InfiniteData<MemoryList> | undefined,
  memory: Memory
): InfiniteData<MemoryList> | undefined {
  if (!data?.pages.length) return data;
  const pages = data.pages.map((page) => ({
    ...page,
    items: page.items.map((m) => (m.id === memory.id ? memory : m)),
  }));
  return { ...data, pages };
}

export function applyMemoryRemovedFromInfiniteData(
  data: InfiniteData<MemoryList> | undefined,
  memoryId: string
): InfiniteData<MemoryList> | undefined {
  if (!data?.pages.length) return data;
  const existedInLoaded = data.pages.some((p) =>
    p.items.some((m) => m.id === memoryId)
  );
  const pages = data.pages.map((page, index) => ({
    ...page,
    items: page.items.filter((m) => m.id !== memoryId),
    total:
      index === 0 && existedInLoaded
        ? Math.max(0, page.total - 1)
        : page.total,
  }));
  return { ...data, pages };
}

export function useInfiniteMemoriesQuery(options: InfiniteMemoriesQueryFilters = {}) {
  const {
    spaceId,
    sortBy = 'created_at',
    sortOrder = 'desc',
    tags,
    source,
    minImportance,
    pageSize = MEMORY_LIST_PAGE_SIZE,
    enabled = true,
  } = options;

  const queryKey = useMemo(
    () =>
      getInfiniteMemoriesQueryKey({
        spaceId,
        sortBy,
        sortOrder,
        tags,
        source,
        minImportance,
        pageSize,
      }),
    [spaceId, sortBy, sortOrder, tags, source, minImportance, pageSize]
  );

  const query = useInfiniteQuery<MemoryList, Error>(
    queryKey,
    async ({ pageParam }) => {
      const page =
        typeof pageParam === 'number' && pageParam >= 1 ? pageParam : 1;
      return apiService.listMemoriesPage({
        page,
        size: pageSize,
        spaceId,
        sortBy,
        sortOrder,
        tags,
        source,
        minImportance,
      });
    },
    {
      enabled: enabled && !!spaceId,
      staleTime: 15_000,
      getNextPageParam: getNextMemoryListPageParam,
    }
  );

  const memories = useMemo(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data]
  );

  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...query,
    memories,
    total,
    queryKey,
  };
}
